/**
 * Frontend optical types.
 *
 * Canonical types (Glass, SurfaceClipping, Radius, Thickness, Conic,
 * SemiDiameter, Pupil, Wavelength, Field, all solve unions) are
 * re-exported from zemax-converter's Zod-derived types — never
 * hand-duplicated. Any rename or shape change in the canonical schemas
 * surfaces here at compile time, which is what we want.
 *
 * Surface composes canonical sub-types with UI-only fields (index,
 * surface_id). SystemData extends canonical LLMSystem with a
 * frontend-only `notes`. AsphereParam stays local because it tolerates
 * string mid-edit values; see follow-up #3 in project_schema_followups.md.
 */

import type React from 'react';
import { fieldTypeToSuffix } from '@/lib/fieldLabels';
import type { Visibility, AnalysisEngine } from '../../contracts/dist/index.js';
import type {
  Glass,
  SurfaceClipping,
  Radius,
  Thickness,
  Conic,
  RadiusSolve,
  ThicknessSolve,
  ConicSolve,
  SemiDiameter,
  InfinityValue,
  Pupil,
  Wavelength,
  Field,
  FieldType,
  LLMSystem,
  LLMOpticalSystem,
  CoordinateBreakCell,
  FreeformTerm, PowerTerm, SuperconicTerm,
  ChebyshevTerm,
  AsphereSurface,
  BiconicZernikeSurface,
  LLMSurface,
  ParaxialSurface,
  ParaxialXySurface,
  TiltedSurface,
  ToroidalSurface,
  BeamSplit,
} from '../../zemax-converter/dist/types/index.js';

// Re-export canonical types for frontend consumers.
export type {
  BeamSplit,
  Glass,
  SurfaceClipping,
  RadiusSolve,
  ThicknessSolve,
  ConicSolve,
  SemiDiameter,
  InfinityValue,
  Pupil,
  Wavelength,
  Field,
  FieldType,
  LLMOpticalSystem,
};

// ── Surface type codes ──

// Derived from the converter's LLMSurfaceSchema union, so a new surface family
// reaches the editor's exhaustive switches as a compile error rather than as a
// code the UI silently has no branch for. COORDBRK is a coordinate break — a
// dummy plane that tilts/decenters everything after it, traced on both engines;
// the LDE renders its six cells in place of radius/glass. XY_POLYNOMIAL is a
// freeform (the OpticStudio Extended Polynomial), Zemax-engine-only; the LDE
// shows its terms read-only — the agent and ZMX import are its authoring paths.
export type SurfaceTypeCode = LLMSurface['type']['code'];

export function isAsphericType(code: SurfaceTypeCode): boolean {
  switch (code) {
    case 'ASPHERE': return true;
    case 'STANDARD': return false;
    case 'COORDBRK': return false;
    case 'XY_POLYNOMIAL': return false;
    case 'CHEBYSHEV': return false;
    case 'ZERNIKE_SAG': return false;
    case 'BICONIC_ZERNIKE': return false;
    // A conic expanded in U/V coefficients: not the editor's polynomial asphere.
    case 'SUPERCONIC': return false;
    // A tilted plane (no sag departure) and a toroid (a freeform here).
    case 'TILTED': return false;
    case 'TOROIDAL': return false;
    // Ideal thin lenses: planes with power, no sag.
    case 'PARAXIAL': return false;
    case 'PARAXIAL_XY': return false;
    // A conic with tolerancing deviations: not the editor's polynomial asphere.
    case 'IRREGULAR': return false;
    // A conic carrying a phase profile: its sag is the conic's.
    case 'ZERNIKE_PHASE': return false;
    // A ruled grating on a conic, a toroid or a plane: no polynomial.
    case 'GRATING': return false;
    // An even asphere carrying a phase: the editor does not edit its params.
    case 'BINARY_OPTIC': return false;
    // Zernike Standard Sag cells plus an imported grid: not the editor's asphere.
    case 'GRID_SAG': return false;
    // A STANDARD shape whose FOLLOWING MEDIUM is the gradient: the sag is the
    // conic's, and the grin cells describe the index, not a polynomial.
    case 'GRADIENT_INDEX': return false;
    // A conic or toroid carrying a hologram: its sag is the substrate's.
    case 'HOLOGRAM': return false;
    // The displayed substrate is a conic (a plane on the 'plane' kind); the
    // facet that bends rays is a separate profile, not the editor's asphere.
    case 'FRESNEL': return false;
  }
}

/** A phase surface (Zernike phase, binary optic, hologram): the base shape
 *  draws as the surface, the phase deflects rays without a sag of its own. Not a freeform. */
export function isPhaseType(code: SurfaceTypeCode): boolean {
  return code === 'ZERNIKE_PHASE' || code === 'BINARY_OPTIC' || code === 'HOLOGRAM';
}

/** A tilted plane: not a freeform (no terms), but off-axis geometry by construction. */
export function isTiltedType(code: SurfaceTypeCode): boolean {
  return code === 'TILTED';
}

/** A Grid Sag: an imported grid of sag points on a Zernike Standard Sag base.
 *  Its true shape comes from OpticStudio's sag, like a freeform's. */
export function isGridSagType(code: SurfaceTypeCode): boolean {
  return code === 'GRID_SAG';
}

/** A gradient-index surface: a STANDARD shape whose following MEDIUM has an
 *  index that varies with position (the cells describe that medium, not a sag). */
export function isGradientIndexType(code: SurfaceTypeCode): boolean {
  return code === 'GRADIENT_INDEX';
}

/** A Fresnel surface: rays land on the displayed substrate (a plane on the
 *  'plane' kind) and bend by a separate facet profile held in the fresnel_*
 *  cells (or the generalized kind's polynomial). */
export function isFresnelType(code: SurfaceTypeCode): boolean {
  return code === 'FRESNEL';
}

export function isCoordinateBreakType(code: SurfaceTypeCode): boolean {
  return code === 'COORDBRK';
}

export function isFreeformType(code: SurfaceTypeCode): boolean {
  return code === 'XY_POLYNOMIAL' || code === 'CHEBYSHEV' || code === 'ZERNIKE_SAG' || code === 'BICONIC_ZERNIKE' || code === 'TOROIDAL';
}

/** A coordinate-break cell (decenter/tilt); value in lens units or degrees.
 *  Canonical, from the converter's Zod schema like every other solve shape. */
export type { CoordinateBreakCell } from '../../zemax-converter/dist/types/index.js';
/** Freeform terms — XY ({i, value, solve}) and Chebyshev ({i, j, value, solve});
 *  canonical from the converter. */
export type { FreeformTerm, ChebyshevTerm, PowerTerm, SuperconicTerm, IrregularCell, GratingCell, GratingKind, DiffractionOrderCell, BinaryOpticPhaseBasis, SurfaceGridRef, GridInterpolation, GrinCell, GrinTerm, GrinProfile, HologramCell, HologramKind, HologramConstruction, FresnelCell, FresnelFacetTerm, FresnelKind } from '../../zemax-converter/dist/types/index.js';
import type { IrregularCell, GratingCell, GratingKind, DiffractionOrderCell, BinaryOpticPhaseBasis, SurfaceGridRef, GridInterpolation, GrinCell, GrinTerm, GrinProfile, HologramCell, HologramKind, HologramConstruction, FresnelCell, FresnelFacetTerm, FresnelKind } from '../../zemax-converter/dist/types/index.js';

// ── Surface (canonical + UI-only fields) ──

/**
 * Frontend Surface — flat editor representation. Sub-types (Radius,
 * Thickness, Glass, SurfaceClipping, SemiDiameter, Conic) are pulled
 * straight from canonical Zod schemas so any rename or shape change in
 * zemax-converter surfaces here at compile time.
 *
 * The shape is flat (not a discriminated union on type.code) because
 * `params` and `norm_radius` are treated as optional everywhere in the
 * editor — STANDARD surfaces may carry stale asphere data in local state
 * while the user toggles type. Sending that stray data on a full-system
 * PATCH is accepted, but it does NOT persist: `normalizeSystem` (run by
 * `finalizeForPersist`) deletes `params`/`norm_radius` from any surface
 * whose `type.code` is not ASPHERE, so the saved system — and the response
 * — comes back without them. (Note: the agent-side `SurfaceChangesSchema`
 * rejects them outright alongside a STANDARD type change — but the editor
 * uses the PATCH path, not modify_surface.)
 *
 * `index` and `surface_id` are UI-only and stripped before PATCH by
 * `sanitizeSurfacesForBackend` in LensDataEditor.tsx. Everything below them is
 * canonical (BaseSurfaceSchema) and is sent.
 */
export interface Surface {
  // UI-only
  index?: number;
  surface_id?: string;

  // Canonical fields
  comment?: string;
  type: { code: SurfaceTypeCode; dll?: string };
  radius: Radius;
  thickness: Thickness;
  semi_diameter: SemiDiameter;
  conic: Conic;
  glass?: Glass;
  coating?: string;
  clipping?: SurfaceClipping;

  // Asphere-specific (optional at frontend level; enforced per-variant by backend)
  params?: AsphereParam[];
  norm_radius?: number;
  // ASPHERE only (Zemax engine): a Q-Type Asphere basis; params[].i is then the
  // Q index m, not a radial power. Absent = the power series.
  basis?: AsphereSurface['basis'];

  // Coordinate-break cells (COORDBRK only; both engines). Optional at the
  // frontend level for the same reason params is.
  decenter_x?: CoordinateBreakCell;
  decenter_y?: CoordinateBreakCell;
  tilt_x?: CoordinateBreakCell;
  tilt_y?: CoordinateBreakCell;
  tilt_z?: CoordinateBreakCell;
  order?: 0 | 1;

  // Freeform terms (XY_POLYNOMIAL / CHEBYSHEV only; Zemax engine). XY shares
  // norm_radius with ASPHERE; Chebyshev carries norm_x / norm_y. Read-only in
  // the editor.
  terms?: Array<FreeformTerm | ChebyshevTerm>;
  norm_x?: number;
  norm_y?: number;
  // ZERNIKE_SAG only: which Zernike numbering `terms[].i` uses, plus the
  // extrapolate flag, the Zernike decenter (fringe / standard) and the
  // obscuration ratio (annular). Read-only in the editor.
  zernike_ordering?: 'fringe' | 'standard' | 'annular';
  extrapolate?: boolean;
  zernike_decenter_x?: number;
  zernike_decenter_y?: number;
  zernike_obscuration?: number;
  // BICONIC_ZERNIKE only: the x radius / conic of the biconic base (the
  // ordinary radius / conic are the y ones) and the raw x / y power series.
  // Read-only in the editor.
  radius_x?: BiconicZernikeSurface['radius_x'];
  conic_x?: BiconicZernikeSurface['conic_x'];
  x_terms?: PowerTerm[];
  y_terms?: PowerTerm[];
  // SUPERCONIC only: the U_k / V_k series on the base conic. Read-only.
  u_terms?: SuperconicTerm[];
  v_terms?: SuperconicTerm[];
  // TILTED only: tan θx / tan θy of the tilted plane. Read-only.
  tangent_x?: TiltedSurface['tangent_x'];
  tangent_y?: TiltedSurface['tangent_y'];
  // TOROIDAL only: the radius of rotation (the radius along x; the ordinary
  // radius / conic are the Y-Z curve); its even y powers ride in y_terms.
  radius_rotation?: ToroidalSurface['radius_rotation'];
  // PARAXIAL only: the ideal lens's focal length and OPD mode; PARAXIAL_XY
  // only: its x / y optical powers (inverse lens units). Read-only.
  focal_length?: ParaxialSurface['focal_length'];
  opd_mode?: ParaxialSurface['opd_mode'];
  power_x?: ParaxialXySurface['power_x'];
  power_y?: ParaxialXySurface['power_y'];
  // IRREGULAR only: the tolerancer's deviation cells. Read-only.
  deviation_decenter_x?: IrregularCell;
  deviation_decenter_y?: IrregularCell;
  deviation_tilt_x?: IrregularCell;
  deviation_tilt_y?: IrregularCell;
  spherical_deviation?: IrregularCell;
  astigmatism_deviation?: IrregularCell;
  coma_deviation?: IrregularCell;
  deviation_angle?: IrregularCell;
  // ZERNIKE_PHASE, GRATING, BINARY_OPTIC and HOLOGRAM: the diffraction order. A ZERNIKE_PHASE's
  // phase coefficients (waves) ride in terms, with zernike_ordering,
  // norm_radius, extrapolate and (annular) zernike_obscuration. Read-only.
  diffraction_order?: DiffractionOrderCell;
  // GRATING only: the base shape and its cells (radius_rotation above on the
  // toroidal kind; diffraction_order above, shared with ZERNIKE_PHASE). Read-only.
  grating_kind?: GratingKind;
  grating_lines_per_um?: GratingCell;
  vls_focal_radius?: GratingCell;
  vls_cos_alpha?: GratingCell;
  vls_cos_beta?: GratingCell;
  vls_construction_wavelength?: GratingCell;
  // BINARY_OPTIC only: the phase polynomial (terms in radians, i by the basis)
  // and Binary Optic 1's absolute flag; its base even asphere rides in params,
  // its order in diffraction_order above. Read-only.
  phase_basis?: BinaryOpticPhaseBasis;
  absolute_xy?: boolean;
  // GRID_SAG only: the imported grid (a reference to the stored points plus
  // their summary) and its interpolation; the Zernike terms ride in terms, the
  // base asphere in params. Read-only.
  grid?: SurfaceGridRef;
  grid_interpolation?: GridInterpolation;
  // GRADIENT_INDEX only: which OpticStudio gradient surface this is, and that
  // profile's cells (the index of the medium AFTER the surface; its dispersion
  // material, where the profile has one, is glass {solve:'gradient', material}).
  // gradient_12's coefficients ride in the three grin_*_terms arrays. Read-only.
  grin_profile?: GrinProfile;
  grin_step?: GrinCell;
  grin_n0?: GrinCell;
  grin_nr1?: GrinCell;
  grin_nr2?: GrinCell;
  grin_nr4?: GrinCell;
  grin_nr6?: GrinCell;
  grin_nr8?: GrinCell;
  grin_nr10?: GrinCell;
  grin_nr12?: GrinCell;
  grin_nz1?: GrinCell;
  grin_nz2?: GrinCell;
  grin_nz3?: GrinCell;
  grin_nz4?: GrinCell;
  grin_nx1?: GrinCell;
  grin_nx2?: GrinCell;
  grin_ny1?: GrinCell;
  grin_ny2?: GrinCell;
  grin_ny3?: GrinCell;
  grin_ny4?: GrinCell;
  grin_ny5?: GrinCell;
  grin_ny6?: GrinCell;
  grin_isoindex_radius?: GrinCell;
  grin_alpha?: GrinCell;
  grin_beta?: GrinCell;
  grin_tan_alpha?: GrinCell;
  grin_tan_beta?: GrinCell;
  grin_offset?: GrinCell;
  grin_decenter_x?: GrinCell;
  grin_decenter_y?: GrinCell;
  grin_tilt_x?: GrinCell;
  grin_tilt_y?: GrinCell;
  grin_capping?: GrinCell;
  grin_x_terms?: GrinTerm[];
  grin_y_terms?: GrinTerm[];
  grin_z_terms?: GrinTerm[];
  // HOLOGRAM only: the substrate and the construction beams' sense, the two
  // construction points and the construction wavelength (radius_rotation and
  // y_terms above on the toroidal kind; diffraction_order above). Read-only.
  hologram_kind?: HologramKind;
  construction_beams?: HologramConstruction;
  construction_x1?: HologramCell;
  construction_y1?: HologramCell;
  construction_z1?: HologramCell;
  construction_x2?: HologramCell;
  construction_y2?: HologramCell;
  construction_z2?: HologramCell;
  construction_wavelength?: HologramCell;
  // FRESNEL only: which OpticStudio Fresnel surface this is, and the FACET
  // that bends rays — its curvature, conic and even-asphere terms (the plane,
  // extended and cylinder kinds), or the generalized kind's polynomial in
  // norm_radius / terms. The SUBSTRATE rays land on is the surface's own
  // radius / conic / params (a plane on the 'plane' kind). Read-only.
  fresnel_kind?: FresnelKind;
  fresnel_curvature?: FresnelCell;
  fresnel_conic?: FresnelCell;
  fresnel_terms?: FresnelFacetTerm[];
}

// ── AsphereParam (local: tolerates string during keystroke editing) ──

/**
 * Asphere coefficient as held in editor state.
 *
 * Diverges from canonical `Param` (zemax-converter): `value` and
 * `solve.scale` accept strings during mid-edit because input.onChange
 * fires per keystroke. The follow-up to migrate NumericInput so it emits
 * `number` only on commit is tracked as project_schema_followups item 3 —
 * once that lands, this can become `Pick<Param, 'i' | 'value' | ...>` and
 * collapse into the canonical type.
 */
export interface AsphereParam {
  i: number;
  value: number | string;
  annotation?: string;
  solve?: {
    type: 'fixed' | 'variable' | 'pickup';
    ref_surface?: number;
    scale?: number | string;
    offset?: number;
  };
}

/** Whether a clipping field has any non-zero extent (i.e. represents a real constraint). */
export function hasClipping(c: SurfaceClipping | undefined): boolean {
  if (!c) return false;
  if (c.type === 'aperture') return c.min_radius > 0 || c.max_radius > 0;
  if (c.type === 'obscuration') return c.inner_radius > 0 || c.outer_radius > 0;
  // The rectangular forms: the schema rejects a non-positive half width, so one
  // that exists is always a real constraint.
  return true;
}

// ── Pupil mode (derived from canonical Pupil) ──

/** All pupil modes (useful for exhaustive checks) */
export type PupilMode = Pupil['mode'];

// ── System (canonical + frontend-only `notes`) ──

/**
 * Frontend SystemData — canonical LLMSystem plus a local `notes` field
 * the editor surfaces but the backend doesn't model.
 */
export type SystemData = LLMSystem & {
  notes?: string;
};

// ── Component prop types ──

/** Transient fields retained while a surface is a coordinate break. Owned by
 * one editor so another design cannot restore its glass or aperture. */
export type CoordinateBreakRestorationCache = Map<string, Pick<Surface, 'glass' | 'clipping' | 'coating' | 'params' | 'norm_radius'>>;

export interface SurfaceTableProps {
  /** Survives table remounts within the owning editor; standalone tables isolate their cache. */
  coordinateBreakRestorationCache?: CoordinateBreakRestorationCache;
  /** Report which cell has focus, as `surface:<row index>:<column>`, or null
   *  on leaving the table. Position, not surface_id: the id is minted
   *  client-side and is absent on a design loaded from the server, so it is
   *  never the same string in two browsers. */
  onFocusCell?: (focusKey: string | null) => void;
  /** Other people on this conversation, for outlining the cell each has
   *  focused in their own colour. */
  peers?: PresenceLike[];
  selfEmail?: string;
  surfaces: Surface[];
  /** `stopIndex`/`globalReferenceSurface` are passed back only when an
   *  insert/delete shifted them; `undefined` means "leave as is". */
  onChange: (surfaces: Surface[], stopIndex?: number, globalReferenceSurface?: number) => void;
  /** An insert/delete shifted the indices after it: `remap` is the shift the
   *  table applied to every solve reference in `surfaces`, and `removed` the
   *  deleted range (absent on an insert). The editor applies the same shift to
   *  whatever else names a surface index (the beam splits). */
  onReindex?: (remap: (index: number) => number, removed?: { from: number; to: number }) => void;
  /** On the primary chain: rows a beam-split arm's solve references (see
   *  `armReferencedPrimaryIndices`), which cannot be deleted — the arm's
   *  pickup would land on whatever shifts into the row's place. */
  armReferencedIndices?: ReadonlySet<number>;
  /** Render one beam-split ARM instead of the primary chain. `surfaces` is then
   *  `[split row, ...arm rows, image]` (see `armTableRows`): row i is projected
   *  index `splitIndex + i`. The split row keeps the primary's geometry
   *  read-only and edits only its thickness (`thickness_after_split`, fixed or
   *  variable); the image row is the primary's and is read-only here; the stop
   *  column is absent (the stop lives in the shared prefix). Insert/delete act
   *  on the arm rows and never on the split or image row. */
  armView?: { arm: number; splitIndex: number; label: string };
  /** On the primary chain: split name per `surface_index`, shown as a badge on
   *  that row, which also cannot be deleted while it carries an arm. */
  splitBadges?: ReadonlyMap<number, string>;
  /** Request a save of the current surfaces. Every editable column commits
   *  through it. */
  onCommit: () => void;
  disabled?: boolean;
  pupilMode: PupilMode;
  stopIndex: number;
  onStopIndexChange: (index: number) => void;
  /** System's `global_reference_surface` (Zemax GLRS), undefined when the system
   *  leaves it at the Zemax default of surface 1. Like `stopIndex` it is a
   *  surface index, so the table shifts it on insert/delete. */
  globalReferenceSurface?: number;
  /** Active analysis engine. Hides Optiland-only glass solves
   * (currently `ideal`) from the dropdown when Zemax. */
  engine?: AnalysisEngine;
  /** System's declared Zemax glass catalogs — passed to the glass-info popover
   * so it can dim entries from catalogs not actually loaded at GCAT. */
  glassCatalogs?: readonly string[];
  /** Optional — when provided, the glass-info popover renders a "use" button on
   * every non-active catalog. Moves the chosen catalog to the FRONT of
   * `glass_catalogs` so the glass resolves from it (Zemax resolves bare glass
   * names by catalog order; there is no per-surface override). */
  onSelectGlassCatalog?: (catalog: string) => void;
  /** Coating names from the system's active custom coating file
   * (`system.coating_file`) — offered as datalist suggestions in the Coat
   * column. Absent for the stock COATING.DAT (its names aren't listed
   * client-side), in which case no suggestions are offered — the cell is
   * free text either way. */
  coatingNames?: readonly string[];
}

/** Conversation access level — single-sourced in contracts/src/vocabulary.ts,
 * shared with src/server/types.ts. */
export { VISIBILITIES } from '../../contracts/dist/index.js';
export type { Visibility } from '../../contracts/dist/index.js';

/** The `projects.visibility.*` message per visibility level. A total table,
 *  so a new level fails the build here instead of rendering a raw key. */
export const VISIBILITY_KEY = {
  private: 'private',
  company: 'company',
  everyone: 'everyone',
} satisfies Record<Visibility, string>;

/** Cap on project names so a runaway paste can't blow out row layouts. The
 *  backend rejects names over 200 chars (MAX_PROJECT_NAME_LENGTH in
 *  src/server/constants.ts); this tighter cap is a UI layout guardrail. */
export const PROJECT_NAME_MAX = 100;

/** All three levels are offered in every visibility select. "Public" (everyone)
 *  means readable by any logged-in user who has the project/conversation link —
 *  it is NOT a discovery surface: the project list query (listProjectsForUser)
 *  is owner-scoped, so a public project never appears in anyone else's list. */

/** Which analysis backend is computing: Optiland (Python ray trace), Zemax
 * (via ZOS-API), or Zemax routed through a BYO bridge tab. Single-sourced in
 * contracts/src/vocabulary.ts, shared with src/server/types.ts. */
export { ANALYSIS_ENGINES, ENGINE_FAMILIES, HOSTED_ENGINE_GRANT, isZemaxEngine, isExternalEngine, engineFamily, engineGrantMissing } from '../../contracts/dist/index.js';
export type { AnalysisEngine, EngineFamily, HostedEngineGrant, HostedEngineGrants } from '../../contracts/dist/index.js';

/** The part of a presence roster entry the editor needs. Structural rather than
 *  an import, so the optical types stay independent of the collab API module. */
export interface PresenceLike {
  user_email: string;
  color_index: number;
  focus_key: string | null;
}

/** The edit handed to a non-direct commit mode. */
export interface PendingSystemEdit {
  system: SystemData;
  surfaces: Omit<Surface, 'index' | 'surface_id'>[];
  /** Present when the design has beam splits (absent and empty mean none). */
  beam_splits?: BeamSplit[];
  /** Version this edit was written against. Both non-direct modes carry it: a
   *  branch is cut from whatever is live, and a proposal is approved against
   *  whatever is live, so both need to detect that the design moved on. */
  baseVersion: number;
}

export type CommitMode =
  | { kind: 'direct' }
  | { kind: 'propose'; submit: (edit: PendingSystemEdit) => Promise<void> }
  | { kind: 'branch'; submit: (edit: PendingSystemEdit) => Promise<void> };

export interface LensDataEditorProps {
  conversationId: string;
  /** Project a NEW conversation is created in when the first save creates the
   *  row (draft chat opened with ?project=); ignored once the row exists. */
  projectId?: string;
  /** The project this conversation actually belongs to. Distinct from
   *  `projectId`, which is placement-only and goes null the moment the row
   *  exists — so anything needing the REAL project (custom catalogs offering a
   *  'This project' scope) must read this instead, or it sees null for every
   *  filed conversation. */
  effectiveProjectId?: string;
  activeEditorTab?: 'system' | 'surface';
  onSystemUpdate?: () => void;
  readOnly?: boolean;
  /** Collaboration mode: the viewer may edit but cannot write to THIS
   *  conversation. A commit hands the edited system here instead of saving in
   *  place — the page forks the conversation and applies the edit to the fork,
   *  then navigates there. In-place-only actions (Sync, vignetting, custom
   *  catalogs) are hidden while this is set, since they would write to the
   *  conversation being viewed. A rejection surfaces as the editor's error. */
  /** What pressing Save does. Three outcomes, because who you are decides
   *  whether an edit lands, is proposed, or starts a branch:
   *
   *  - `direct`  — the owner, or the editor currently holding the pen. Saves.
   *  - `propose` — an invited editor who does not hold the pen. The edit goes
   *                to whoever does, for approval.
   *  - `branch`  — anyone else who chose to branch. Cuts their own copy.
   *
   *  In the two non-direct modes the editor writes nothing itself: it hands the
   *  sanitized edit to `submit` and lets the page decide what to do with it.
   *  In-place-only actions (Zemax Sync, vignetting, catalog upload) are hidden
   *  in those modes, since they would write to the conversation being viewed. */
  commitMode?: CommitMode;
  /** Called as the person moves between cells, with a key identifying the one
   *  they are in (or null on leaving). Feeds presence so the others can see
   *  where they are working. */
  onFocusCell?: (focusKey: string | null) => void;
  /** Everyone else currently on this conversation, so their focused cell can be
   *  outlined in their colour. */
  peers?: PresenceLike[];
  /** The viewer's own address, so they are not shown as their own peer. */
  selfEmail?: string;
  actionBarRightElement?: React.ReactNode;
  /** Incrementing counter that triggers a system reload (driven by agent system updates) */
  refreshTrigger?: number;
  /** Active analysis engine — controls the engine-specific Sync button.
   * When 'zemax', the LDE shows a Sync action that round-trips the current
   * system through ZOS-API to refresh post-iteration cached values
   * (auto-SDs, marginal-ray-driven curvatures, etc.) without persisting. */
  engine?: AnalysisEngine;
}

/**
 * The Optiland paraxial trace a pupil-mode or field-type switch restates its
 * value with. Lengths in mm; `epl` is the entrance pupil location measured
 * from surface 1 along +z (optiland `paraxial.EPL`).
 */
export interface PupilParaxialSeed {
  efl: number | null;
  /** Infinite-conjugate paraxial F/# (EFL / EPD). */
  fno: number | null;
  /** Paraxial working F/# at the system's own conjugates. */
  working_fno: number | null;
  epd: number | null;
  epl: number | null;
}

/** The paraxial seed, or the reason there is none (shown by the value a switch left blank). */
export type ParaxialSeedResult = { ok: true; seed: PupilParaxialSeed } | { ok: false; reason: string };

export interface ObjectSpace {
  /** Object-to-surface-1 distance in mm; null for an object at infinity. */
  distance: number | null;
  /** The object surface carries a glass, so the object-space index is not 1. */
  immersed: boolean;
}

export interface SystemEditorProps {
  system: SystemData;
  onChange: (system: SystemData) => void;
  onCommit?: () => void;
  disabled?: boolean;
  /** Fetches the paraxial seed when a pupil-mode or field-type switch needs
   *  it; without one the switch leaves the value blank and shows the reason. */
  loadParaxial: () => Promise<ParaxialSeedResult>;
  objectSpace?: ObjectSpace;
  /** Active analysis engine; the pupil modes it refuses are greyed. */
  engine?: AnalysisEngine;
  glassCatalogElement?: React.ReactNode;
  /** Rendered in the Fields section title bar, left of the field-type select
   *  (auto-calc vignetting button + result chip, owned by LensDataEditor). */
  fieldsActionElement?: React.ReactNode;
  /** Whether the Advanced section is expanded. LensDataEditor owns it because every reload after a save
   *  replaces the editor with its loading screen, which would reset state held here. */
  advancedOpen?: boolean;
  onAdvancedOpenChange?: (open: boolean) => void;
}

// ── Helpers ──

/**
 * Format a single field coordinate with units, e.g. "14°" for angle fields,
 * "5 mm" for height fields. Returns the bare coordinate without a "Field N"
 * prefix — for that, see `lib/fieldLabels.ts:formatFieldLabel`.
 *
 * Units come from `fieldTypeToSuffix`, the display convention shared with
 * `useFieldControl` and `formatFieldLabel`.
 */
export function formatFieldCoord(
  field: { x: number; y: number },
  fieldType: string
): string {
  const unit = fieldTypeToSuffix(fieldType);
  if (field.x === 0) return `${field.y}${unit}`;
  return `(${field.x}, ${field.y})${unit}`;
}
